import { useEffect, useState } from 'react';
import { ButtonData } from '../../../../../components/buttonGroup/ButtonGroup';
import RefundModal from '../../../../../components/modals/RefundModal';
import { DateFilterField } from '../../../../../components/tables/filterForms/DateFilterField';
import { FilterField, FilterForm } from '../../../../../components/tables/filterForms/FilterForm';
import { FormSelect } from '../../../../../components/tables/filterForms/FormSelect';
import { SearchField } from '../../../../../components/tables/filterForms/SearchField';
import SFCTable from '../../../../../components/tables/SFCTable';
import { useToast } from '../../../../../context/ToastContext';
import { useListFiltersInUrl } from '../../../../../hooks';
import {
  useBatchRefundRegistrationMutation,
  useGetRegistrationsQuery
} from '../../../../../services/endpoints/content/registrations';
import { Scopes } from '../../../../../services/endpoints/people/user';
import { registration } from '../../../../../types/registrations.type';
import { registrationsColumns } from '../../../../../utils/consts/columnDefs/registrations';
import {
  getFilterValue,
  parseRegistrationsDeepLinkFilters,
  preserveFilterFields
} from '../../../../../utils/listFilterSearchParams';

const URL_FILTER_FIELDS = ['course_id', 'order_id'];

export const RegistrationsView = () => {
  const defaultScope = 'completed';
  const { addToast } = useToast();
  const [filters, setFilters] = useListFiltersInUrl(
    { scope: defaultScope, data: { page: 1, entries: 10 } },
    { parseExtra: parseRegistrationsDeepLinkFilters }
  );
  const hasUrlFilter = !!filters.data?.filterBy?.some(
    (f) => f.field === 'course_id' || f.field === 'order_id'
  );
  const [scopes, setScopes] = useState<Scopes>({});
  const { data, error, isLoading, isFetching, refetch } = useGetRegistrationsQuery(filters, {
    refetchOnMountOrArgChange: true
  });
  const [buttons, setButtons] = useState<ButtonData[][]>();
  const [showModal, setShowModal] = useState(false);
  const [showFullRefundModal, setShowFullRefundModal] = useState(false);
  const [selectedIDs, setSelectedIDs] = useState<number[]>([]);
  const [batchRefundRegistration] = useBatchRefundRegistrationMutation();
  const registration = (data?.results ?? []) as registration[];

  const registrationStatus: { [key: string]: string } = {
    active: 'Active',
    inactive: 'Inactive'
  };

  const filterFormFields: FilterField[] = [
    {
      name: 'session_title',
      element: (
        <SearchField
          name="session_title"
          label="Session"
          placeholder="Search session"
        />
      ),
      value: getFilterValue(filters.data?.filterBy, 'session_title')
    },
    {
      name: 'child_first_name',
      label: 'Child first name',
      type: 'text',
      value: getFilterValue(filters.data?.filterBy, 'child_first_name')
    },
    {
      name: 'child_last_name',
      label: 'Child Last name',
      type: 'text',
      value: getFilterValue(filters.data?.filterBy, 'child_last_name')
    },
    {
      name: 'parent_first_name',
      label: 'Parent First name',
      type: 'text',
      value: getFilterValue(filters.data?.filterBy, 'parent_first_name')
    },
    {
      name: 'parent_last_name',
      label: 'Parent Last name',
      type: 'text',
      value: getFilterValue(filters.data?.filterBy, 'parent_last_name')
    },
    {
      name: 'active_status',
      placeholder: 'Any',
      element: (
        <FormSelect
          name="active_status"
          label="Active Status"
          options={Object.entries(registrationStatus).map((active_status) => ({
            value: active_status[0],
            label: active_status[1]
          }))}
          selected_Value={Object.entries(registrationStatus)
            .map(([value, label]) => ({ value, label }))
            .find(
              (option) => option.value === getFilterValue(filters.data?.filterBy, 'active_status')
            )}
        />
      )
    },
    {
      name: 'created_at_from',
      id: 'created_at',
      operator: 'greater_equal_than',
      element: (
        <DateFilterField
          name="created_at_from"
          label="Created At"
        />
      ),
      value: getFilterValue(filters.data?.filterBy, 'created_at', 'greater_equal_than')
    },
    {
      name: 'created_at_to',
      id: 'created_at',
      operator: 'lesser_equal_than',
      element: <DateFilterField name="created_at_to" />,
      value: getFilterValue(filters.data?.filterBy, 'created_at', 'lesser_equal_than')
    }
  ];

  useEffect(() => {
    if (!error && !isLoading && data) {
      if (data.scopes) {
        setScopes(
          data.scopes.reduce((acc: Scopes, curr: Scopes) => ({ ...acc, ...curr }), {} as Scopes)
        );
        setButtons(
          data.scopes.map((sMap) =>
            Object.entries(sMap).map(([scope, title]) => {
              let scope_count = 0;
              if (data.scope_counts != null) {
                scope_count = data.scope_counts[scope] ?? 0;
              }
              return {
                title: `${title} (${scope_count})`,
                key: scope
              };
            })
          )
        );
      }
    } else if (error) {
      addToast('Error while loading data', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isLoading, isFetching]);

  useEffect(() => {
    refetch();
  }, []);

  const setFilterBy = (filterBy: { operator: string; field: string; value: string }[]) => {
    setFilters((prev) => ({
      ...prev,
      data: {
        ...prev!.data,
        filterBy: preserveFilterFields(filterBy, prev.data?.filterBy, URL_FILTER_FIELDS)
      }
    }));
  };

  const handleBatchRefund = (data: any) => {
    if (selectedIDs.length > 0) {
      batchRefundRegistration(data)
        .unwrap()
        .then((res) => {
          addToast('Refund applied.', 'success');
          setSelectedIDs([]);
          window.location.reload();
        });
    }
  };
  return (
    <div className="py-2">
      <SFCTable
        name={`${scopes[filters?.scope ?? defaultScope] ?? 'Completed'} Registration`}
        columns={registrationsColumns}
        data={registration}
        count={data?.count}
        defaultScope={filters.scope ?? defaultScope}
        scopes={buttons}
        isLoading={isLoading}
        isFetching={isFetching}
        filters={filters?.data?.filterBy}
        indexDownloadPath="registrations"
        selectableRows={true}
        showScope={!hasUrlFilter}
        batchActions={[
          {
            name: 'Partial Refund Selected',
            fn: (selectedRowsIDs: number[]) => {
              setSelectedIDs(selectedRowsIDs);
              setShowModal(true);
            }
          },
          {
            name: 'Full Refund Selected',
            fn: (selectedRowsIDs: number[]) => {
              setSelectedIDs(selectedRowsIDs);
              setShowFullRefundModal(true);
            }
          }
        ]}
        filterForm={
          <FilterForm
            fields={filterFormFields}
            onApplyFilters={setFilterBy}
          />
        }
        onFiltersChanged={(newfilters) => {
          if (typeof newfilters !== 'function') return;

          setFilters((prev) => {
            const modifiedFilters = newfilters(prev);
            if (modifiedFilters?.data) {
              modifiedFilters.data.filterBy = preserveFilterFields(
                modifiedFilters.data.filterBy || [],
                prev.data?.filterBy,
                URL_FILTER_FIELDS
              );
            }
            return modifiedFilters;
          });
        }}
      />
      <RefundModal
        show={showModal}
        title="Complete Refund Details Below:"
        fullRefund={false}
        onClose={() => setShowModal(false)}
        onSubmit={(refundAmount, notes, deactivateAll) => {
          if (refundAmount !== null && !isNaN(refundAmount)) {
            setShowModal(false);
            handleBatchRefund({
              ids: selectedIDs,
              refund_notes: notes ?? '',
              deactivate: deactivateAll,
              total: refundAmount as number
            });
          }
        }}
      />

      <RefundModal
        show={showFullRefundModal}
        title="Complete Refund Details Below:"
        fullRefund={true}
        onClose={() => setShowFullRefundModal(false)}
        onSubmit={(refundAmount, notes, deactivateAll) => {
          setShowFullRefundModal(false);
          handleBatchRefund({
            ids: selectedIDs,
            refund_notes: notes ?? '',
            deactivate: deactivateAll
          });
        }}
      />
    </div>
  );
};
